// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/RequesterCheckField/index.tsx

import { CheckboxField } from '@modusign/react-signing/types';
import cx from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import {
  toAbsoluteNumber,
  toAbsolutePoint,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import UniversalCheckboxField from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/UniversalCheckboxField';
import {
  AbsoluteNumber,
  AbsolutePoint,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import { RequesterFieldBaseProps } from '../props';
import RequesterCheckboxFieldContextMenu from './RequesterCheckboxFieldContextMenu';

import './RequesterCheckboxField.scss';

interface RequesterCheckboxFieldProps extends RequesterFieldBaseProps<CheckboxField> {
  fieldNumber?: number;
  onCheckedChange: (checked: boolean) => void;
}

export function RequesterCheckboxField({
  field,
  viewport,
  readOnly,
  isPreview,
  initialContextMenuOpened,
  isDataLabelEditable,
  isDataLabelDuplicated,
  fieldNumber,
  onCheckedChange,
  onSizeChange,
  onPointChange,
  onFieldRemove,
  onDataLabelChange,
}: RequesterCheckboxFieldProps) {
  const [contextMenuOpen, setContextMenuOpen] = useState(initialContextMenuOpened);
  const [focused, setFocused] = useState(false);
  const [isCheckChangeable, setIsCheckChangeable] = useState(
    () => !isDataLabelEditable || (isDataLabelEditable && initialContextMenuOpened)
  );
  const isInMobileBreakpoint = useResponsiveValue([true, false]);

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [isDataLabelDuplicated, field?.dataLabel]);

  const onChangeCheck = (check: boolean) => {
    /**
     * API 권한 이용자는 컨텍스트 메뉴 오픈, 체크 토글을 모두 마우스 이벤트로 처리하고 있어서
     * 컨텍스트가 닫힌 상태에는 체크를 토글하지 않고 그 이후에 체크 토글이 적용되게 수정합니다.
     * 여기서 체크 필드 이동, 사이즈 조절은 컨텍스트 메뉴가 열리지 않은 상태에도 가능하게
     * onChange에서 check 값만 변경하지 않게 처리합니다.
     */
    if (field && field.input) {
      onCheckedChange?.(isCheckChangeable ? check : field.input.value);
    }

    if (!isCheckChangeable && isDataLabelEditable) {
      setIsCheckChangeable(true);
    }
  };

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const handleSizeChange = useCallback(
    (size: AbsoluteNumber) => {
      const relative = toRelativeSize(createAbsoluteSize({ width: size, height: size }), viewport);
      onSizeChange(relative);
    },
    [onSizeChange, viewport]
  );

  const handlePointChange = useCallback(
    (point: AbsolutePoint) => {
      const relative = toRelativePoint(point, viewport);
      onPointChange(relative);
    },
    [onPointChange, viewport]
  );

  if (!field.input) {
    return null;
  }

  return (
    <UniversalCheckboxField
      boxClassName={
        isPreview ? 'RequesterCheckboxField__box--preview' : 'RequesterCheckboxField__box'
      }
      boxStyle={undefined}
      check={field.input.value}
      checkable={!readOnly && !isInMobileBreakpoint}
      dragCancelSelectors={[
        '.RequesterCheckboxField__context-menu-container',
        '.RequesterCheckboxField__delete-button-box',
      ]}
      draggable={!readOnly && !isInMobileBreakpoint}
      point={toAbsolutePoint(field.position, viewport)}
      resizable={!readOnly && !isInMobileBreakpoint}
      rootClassName={cx({
        RequesterCheckboxField: true,
        'RequesterCheckboxField__custom-resize-handler': true,
        'RequesterCheckboxField--checked': field.input.value && !readOnly && !isInMobileBreakpoint,
        'RequesterCheckboxField--focused': focused,
      })}
      size={toAbsoluteNumber(field.size.width, viewport)}
      viewport={viewport}
      onBlur={handleBlur}
      onChangeCheck={onChangeCheck}
      onChangePoint={handlePointChange}
      onChangeSize={handleSizeChange}
      onClick={() => {
        setContextMenuOpen(true);
      }}
      onFocus={handleFocus}
      onOutsideClick={() => {
        if (field.dataLabel && !isDataLabelDuplicated) {
          setContextMenuOpen(false);
        }
        setIsCheckChangeable(!isDataLabelEditable);
      }}
    >
      {!!fieldNumber && <div className="RequesterCheckboxField__field-number">{fieldNumber}</div>}
      {!readOnly && !isInMobileBreakpoint && (
        <>
          {isDataLabelEditable && (
            <div className="RequesterCheckboxField__context-menu-container">
              {contextMenuOpen && (
                <RequesterCheckboxFieldContextMenu
                  dataLabel={field.dataLabel}
                  dataLabelErrorMessage={dataLabelErrorMessage}
                  onChange={onDataLabelChange}
                />
              )}
            </div>
          )}
          <div className="RequesterCheckboxField__delete-button-box">
            <RoundedXButton onClick={() => onFieldRemove(field.id)} />
          </div>
        </>
      )}
    </UniversalCheckboxField>
  );
}
